import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import type { ClientLifecycle, PlanOffer, SubscriptionEvent } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { DateLisible } from '../components/Nombre'
import { Alert, Badge, Button, Card, DataTable, FormActions, Grid, SelectField, Stack, TextField, useToast } from '../components/ui'
import type { Column, SelectOption } from '../components/ui'
import { nomFormule } from './formules'

const VIVANTS = ['trial', 'active', 'past_due']

/** Ce qu'un état photographié par la base dit, en une ligne lisible. */
function lire(etat: string | null): Record<string, unknown> {
  if (etat === null) return {}
  try {
    return JSON.parse(etat) as Record<string, unknown>
  } catch {
    return {}
  }
}

/**
 * Le cycle de vie de l'abonnement d'un client (migration 0077) : ce qui arrivera tout seul,
 * ce qui est programmé, et ce qui est déjà arrivé.
 *
 * CE QUE LA CARTE DIT D'ABORD, C'EST LA DATE. « Paiement attendu » ne suffit pas au
 * super-administrateur qui a le client au téléphone : il lui faut le jour où l'écran du client
 * passera en lecture seule, et ce qu'il doit faire pour l'éviter — réactiver dès réception.
 *
 * PROGRAMMER N'EST PAS CHANGER. Le changement programmé attend l'échéance : le client garde ce
 * qu'il a payé jusqu'au bout. Le changement immédiat reste dans la carte de l'abonnement, au-dessus.
 *
 * L'HISTORIQUE DIT QUI A AGI : le panneau (un humain) ou la tâche journalière (personne). Une
 * suspension que personne n'a décidée à la main doit se lire comme telle.
 */
export function ClientLifecycleCard({
  organizationId,
  plans,
  onChange,
}: {
  organizationId: number
  plans: PlanOffer[]
  onChange: () => void
}) {
  const { t, language, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()

  const token = session?.token ?? ''

  const [cycle, setCycle] = useState<ClientLifecycle | null>(null)
  const [historique, setHistorique] = useState<SubscriptionEvent[]>([])
  const [chargement, setChargement] = useState(true)
  const [envoi, setEnvoi] = useState(false)
  const [planVise, setPlanVise] = useState('')
  const [motifChangement, setMotifChangement] = useState('')
  const [motifResiliation, setMotifResiliation] = useState('')

  const charger = useCallback(() => {
    if (token === '') return

    setChargement(true)

    api
      .getPlatformClientLifecycle(token, organizationId)
      .then((rendu) => {
        setCycle(rendu.lifecycle)
        setHistorique(rendu.events)
      })
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
      .finally(() => setChargement(false))
  }, [token, organizationId, notify, describeError])

  useEffect(charger, [charger])

  const agir = async (acte: () => Promise<unknown>, message: Parameters<typeof t>[0]) => {
    setEnvoi(true)
    try {
      await acte()
      notify(t(message), 'succes')
      setMotifChangement('')
      setMotifResiliation('')
      charger()
      onChange()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const libelleStatut = (statut: unknown) =>
    typeof statut === 'string' ? t(`platform.subscription.${statut}` as never) : ''

  const colonnes: Column<SubscriptionEvent>[] = [
    { key: 'when', header: t('platform.client.lifecycle.when'), render: (e) => <DateLisible valeur={e.occurredAt} /> },
    {
      key: 'what',
      header: t('platform.client.lifecycle.what'),
      render: (e) => {
        const avant = lire(e.previousState)
        const apres = lire(e.newState)
        const detail =
          e.eventType === 'status_changed'
            ? `${libelleStatut(avant.status)} → ${libelleStatut(apres.status)}`
            : e.eventType === 'plan_changed'
              ? `${String(avant.plan_code ?? '')} → ${String(apres.plan_code ?? '')}`
              : e.eventType === 'plan_change_scheduled'
                ? `${String(apres.pending_plan_code ?? '')} · ${String(apres.pending_change_on ?? '')}`
                : e.eventType === 'period_changed'
                  ? `${String(avant.current_period_end ?? '—')} → ${String(apres.current_period_end ?? '—')}`
                  : ''
        return (
          <span className="flex flex-col">
            <span>{t(`platform.client.lifecycle.event.${e.eventType}` as never)}</span>
            {detail !== '' && (
              <span className="text-[0.8rem] text-texte-doux" dir="ltr">
                {detail}
              </span>
            )}
            {e.reason !== null && <span className="text-[0.8rem] text-texte-doux">{e.reason}</span>}
          </span>
        )
      },
    },
    {
      key: 'who',
      header: t('platform.client.lifecycle.who'),
      render: (e) => (
        <Badge tone={e.source === 'job' ? 'info' : 'succes'}>
          {t(e.source === 'job' ? 'platform.client.lifecycle.byJob' : 'platform.client.lifecycle.byPlatform')}
        </Badge>
      ),
    },
  ]

  const optionsPlans: SelectOption[] = [
    { value: '', label: t('platform.client.lifecycle.choosePlan') },
    ...plans.map((p) => ({ value: p.code, label: nomFormule(language, p) })),
  ]

  const vivant = cycle !== null && VIVANTS.includes(cycle.status)

  return (
    <Card title={t('platform.client.lifecycle')}>
      <Stack gap={3}>
        <p className="m-0 text-[0.85rem] text-texte-doux">{t('platform.client.noExpiry')}</p>

        {chargement ? (
          <p className="text-texte-doux">{t('common.loading')}</p>
        ) : cycle === null ? (
          <p className="m-0 text-texte-doux">{t('platform.client.lifecycle.none')}</p>
        ) : (
          <Stack gap={3}>
            {cycle.pastDueSince !== null ? (
              <Alert tone="avertissement">
                {t('platform.client.lifecycle.pastDue')} <DateLisible valeur={cycle.pastDueSince} />.{' '}
                {t('platform.client.lifecycle.readOnlyOn')} <DateLisible valeur={cycle.graceEndsOn} />.{' '}
                {t('platform.client.lifecycle.reactivateHint')}
              </Alert>
            ) : cycle.status === 'trial' ? (
              <Alert tone="info">
                {t('platform.client.lifecycle.trialUntil')} <DateLisible valeur={cycle.trialEndsOn} />.
              </Alert>
            ) : vivant ? (
              <Alert tone="info">
                {t('platform.client.lifecycle.nextEnd')} <DateLisible valeur={cycle.currentPeriodEnd} /> —{' '}
                {t(cycle.autoRenew && !cycle.cancelAtPeriodEnd ? 'platform.client.lifecycle.willRenew' : 'platform.client.lifecycle.willEnd')}
              </Alert>
            ) : null}

            {vivant && (
              <Grid min="18rem">
                <Stack gap={2}>
                  <span className="text-[0.85rem] font-medium">{t('platform.client.lifecycle.scheduled')}</span>
                  {cycle.pendingPlanCode !== null ? (
                    <>
                      <p className="m-0">
                        {t('platform.client.lifecycle.switchTo')} <b>{cycle.pendingPlanName ?? cycle.pendingPlanCode}</b>{' '}
                        {t('platform.client.lifecycle.on')} <DateLisible valeur={cycle.pendingChangeOn} />
                      </p>
                      <FormActions>
                        <Button
                          variant="secondaire"
                          disabled={envoi}
                          onClick={() =>
                            void agir(() => api.cancelPlatformPlanChange(token, organizationId), 'platform.client.lifecycle.changeCancelled')
                          }
                        >
                          {t('platform.client.lifecycle.cancelChange')}
                        </Button>
                      </FormActions>
                    </>
                  ) : (
                    <>
                      <SelectField label={t('platform.client.lifecycle.targetPlan')} value={planVise} options={optionsPlans} onChange={setPlanVise} />
                      <TextField label={t('platform.client.reason')} value={motifChangement} onChange={setMotifChangement} />
                      <FormActions>
                        <Button
                          variant="secondaire"
                          disabled={planVise === '' || envoi}
                          onClick={() =>
                            void agir(
                              () =>
                                api.schedulePlatformPlanChange(
                                  token,
                                  organizationId,
                                  planVise,
                                  motifChangement.trim() === '' ? null : motifChangement.trim(),
                                ),
                              'platform.client.lifecycle.changeScheduled',
                            )
                          }
                        >
                          {t('platform.client.lifecycle.scheduleChange')}
                        </Button>
                      </FormActions>
                    </>
                  )}
                </Stack>

                <Stack gap={2}>
                  <span className="text-[0.85rem] font-medium">{t('platform.client.lifecycle.cancellation')}</span>
                  {cycle.cancelAtPeriodEnd ? (
                    <>
                      <Alert tone="avertissement">
                        {t('platform.client.lifecycle.cancelScheduled')} {cycle.cancelReason}
                      </Alert>
                      <FormActions>
                        <Button
                          variant="secondaire"
                          disabled={envoi}
                          onClick={() =>
                            void agir(
                              () => api.setPlatformCancelAtPeriodEnd(token, organizationId, false, null),
                              'platform.client.lifecycle.cancellationWithdrawn',
                            )
                          }
                        >
                          {t('platform.client.lifecycle.withdrawCancellation')}
                        </Button>
                      </FormActions>
                    </>
                  ) : (
                    <>
                      <TextField label={t('platform.client.reason')} value={motifResiliation} onChange={setMotifResiliation} required />
                      <FormActions>
                        <Button
                          variant="danger"
                          disabled={motifResiliation.trim() === '' || envoi}
                          onClick={() =>
                            void agir(
                              () => api.setPlatformCancelAtPeriodEnd(token, organizationId, true, motifResiliation.trim()),
                              'platform.client.lifecycle.cancellationScheduled',
                            )
                          }
                        >
                          {t('platform.client.lifecycle.cancelAtEnd')}
                        </Button>
                      </FormActions>
                    </>
                  )}
                </Stack>
              </Grid>
            )}

            <span className="text-[0.85rem] font-medium">{t('platform.client.lifecycle.history')}</span>
            <DataTable
              columns={colonnes}
              rows={historique}
              rowKey={(e) => `${e.occurredAt}-${e.eventType}`}
              emptyLabel={t('platform.client.lifecycle.noHistory')}
            />
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
